import { FC, useCallback, useMemo } from "react";
import { FilterColumnItem } from "@vviinn/user-managment-client-ts/dist/models/FilterColumnItem";

import { ChangeSelectHandler } from "../../../helpers/functions/types";
import { FiltersSwitch } from "../../../components/Interaction/FiltersSwitch";

import { CampaignCommonProps } from "../types";
import { useAppSelector } from "../../../store";
import { filterSelectItemLabel } from "../../helpers/filterSelectItemLabel";
import { NamedSelect } from "../../../ui/layout/molecules/NamedSelect";
import {
  ATTRIBUTE_GROUP_STYLES,
  GROUP_STYLES,
} from "../../../ui/layout/detailedPages/styles";
import { selectSettingsTitles } from "../../constants";
import { availableInterfaceFilters } from "../../helpers/availableInterfaceFilters";
import { SubSectionWithStyles } from "../../../ui/layout";

export const NewCampaignInterfaceControls: FC<
  CampaignCommonProps & {
    isTextSearchCampaign: boolean;
  }
> = ({ currentValues, updateBodyField, isTextSearchCampaign }) => {
  const defaultFilters = useAppSelector((state) => state.campaigns.filters);

  const customFieldsTitles = useAppSelector(
    (state) => state.site.customFieldsTitles,
  );

  const availableCampaignFilters = useMemo(
    () =>
      isTextSearchCampaign
        ? [...defaultFilters, ...customFieldsTitles]
        : defaultFilters,
    [defaultFilters, customFieldsTitles, isTextSearchCampaign],
  );

  const { booleanInterfaceControls, valuedInterfaceControls } = useMemo(() => {
    return availableInterfaceFilters(availableCampaignFilters);
  }, [availableCampaignFilters]);

  const updateFilters = useCallback(
    (newFilters: FilterColumnItem[]) =>
      updateBodyField({ enabledFilterColumns: newFilters }),
    [updateBodyField],
  );

  const setBooleanFilter = (filter: string, value: boolean) => {
    const enabledFilterColumns = currentValues?.enabledFilterColumns ?? [];
    const newFilters = value
      ? [
          ...enabledFilterColumns,
          {
            column: filter,
            label: filterSelectItemLabel(filter),
            isPrimary: false,
          },
        ]
      : enabledFilterColumns.filter(
          (enabledFilter) => enabledFilter.column !== filter,
        );
    updateFilters(newFilters);
  };

  const valuedCurrentValue = (columnsGroup: string[]) =>
    currentValues?.enabledFilterColumns?.find((item) =>
      columnsGroup.includes(item.column),
    )?.column;

  const onValuedFilterChange: ChangeSelectHandler = (target) => {
    const { value, name } = target;
    const relevantFilterGroup =
      valuedInterfaceControls.find(
        ({ columnGroupLabel }) => columnGroupLabel === name,
      )?.columnsGroup ?? [];
    const enabledFilterColumns = currentValues?.enabledFilterColumns ?? [];

    const newFilters = enabledFilterColumns.filter(
      ({ column }) => column !== value && !relevantFilterGroup.includes(column),
    );
    if (value)
      newFilters.push({
        column: value,
        label: filterSelectItemLabel(value),
        isPrimary: false,
      });
    updateFilters(newFilters);
  };

  return (
    <SubSectionWithStyles title="Interface Controls" isSubHeader={false}>
      {valuedInterfaceControls.map(({ columnsGroup, columnGroupLabel }) => (
        <NamedSelect
          title={
            selectSettingsTitles[
              columnGroupLabel as keyof typeof selectSettingsTitles
            ] ?? columnGroupLabel
          }
          options={columnsGroup.map((item) => ({
            value: item.length ? item : undefined,
            title: filterSelectItemLabel(item),
          }))}
          customId={columnGroupLabel}
          value={valuedCurrentValue(columnsGroup)}
          onChange={onValuedFilterChange}
          styleVariant="newPage"
          key={columnGroupLabel}
        />
      ))}
      {booleanInterfaceControls && (
        <div className={`${GROUP_STYLES} ${ATTRIBUTE_GROUP_STYLES} p-4`}>
          {booleanInterfaceControls.map((filter) => (
            <FiltersSwitch
              key={filter}
              title={filterSelectItemLabel(filter)}
              value={
                !!currentValues?.enabledFilterColumns?.some(
                  ({ column }) => column === filter,
                )
              }
              onChange={(value) => setBooleanFilter(filter, value)}
            />
          ))}
        </div>
      )}
    </SubSectionWithStyles>
  );
};
